import { Component } from '@theme/component';

const STORAGE_KEY = 'tt_vehicle';

/**
 * Reads the vehicle the shopper last picked.
 *
 * Anything malformed is treated as "no vehicle" rather than thrown, because a
 * corrupt localStorage entry must not take the finder down with it — the
 * shopper would have no way to clear it.
 *
 * @returns {{make: string, model: string, year: number} | null}
 */
export function readVehicle() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v?.make || !v?.model) return null;
    return { make: String(v.make), model: String(v.model), year: Number(v.year) || 0 };
  } catch {
    return null;
  }
}

/** @param {{make: string, model: string, year: number} | null} vehicle */
export function writeVehicle(vehicle) {
  try {
    if (vehicle) localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing. The finder still works, it just won't remember.
  }
}

/**
 * The storefront filter params a vehicle resolves to. Shared with the vehicle
 * bar so "clear" can strip exactly what "see what fits" applied.
 */
export const VEHICLE_FILTER_PARAMS = {
  make: 'filter.p.m.custom.vehicle_make',
  model: 'filter.p.m.custom.vehicle_model',
  year: 'filter.p.m.custom.fitment_year',
};

/**
 * Builds the natively-filtered collection URL for a vehicle.
 *
 * Results come from Shopify's own storefront filters, not from the index —
 * the index is only good enough to populate dropdowns, and pretending
 * otherwise is how a shopper ends up on a page listing kits that don't fit.
 *
 * @param {string} collectionUrl
 * @param {{make: string, model: string, year: number}} vehicle
 */
export function fitmentUrl(collectionUrl, { make, model, year }) {
  const url = new URL(collectionUrl, window.location.origin);
  url.searchParams.set(VEHICLE_FILTER_PARAMS.make, make);
  url.searchParams.set(VEHICLE_FILTER_PARAMS.model, model);
  if (year) url.searchParams.set(VEHICLE_FILTER_PARAMS.year, String(year));
  return url.pathname + url.search;
}

/**
 * Vehicle finder — three cascading selects over a prebuilt fitment index.
 *
 * @typedef {Object} Refs
 * @property {HTMLSelectElement} make
 * @property {HTMLSelectElement} model
 * @property {HTMLSelectElement} year
 * @property {HTMLButtonElement} submit
 *
 * @extends {Component<Refs>}
 */
class VehicleFinder extends Component {
  requiredRefs = ['make', 'model', 'year', 'submit'];

  /** @type {Record<string, Record<string, number[]>>} */
  #index = {};

  connectedCallback() {
    super.connectedCallback();

    this.refs.make.addEventListener('change', this.#onMakeChange);
    this.refs.model.addEventListener('change', this.#onModelChange);
    this.refs.year.addEventListener('change', this.#syncSubmit);
    this.refs.submit.addEventListener('click', this.#onSubmit);

    this.#loadIndex();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.make.removeEventListener('change', this.#onMakeChange);
    this.refs.model.removeEventListener('change', this.#onModelChange);
    this.refs.year.removeEventListener('change', this.#syncSubmit);
    this.refs.submit.removeEventListener('click', this.#onSubmit);
  }

  get #collectionUrl() {
    return this.dataset.collectionUrl || '/collections/all';
  }

  async #loadIndex() {
    const src = this.dataset.indexUrl;
    if (!src) return;

    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`${response.status}`);
      this.#index = await response.json();
    } catch {
      // Leave the finder disabled rather than showing empty dropdowns that
      // look interactive but resolve to nothing.
      this.refs.submit.disabled = true;
      this.refs.submit.textContent = this.dataset.errorText || 'Vehicle list unavailable';
      return;
    }

    this.#fillMakes();
  }

  #fillMakes() {
    // The page's own filter state outranks memory: on a URL already filtered
    // to a vehicle, the dropdowns must show that vehicle, not whichever truck
    // localStorage last saw — the finder is the page's face for its filters.
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {
      make: params.get(VEHICLE_FILTER_PARAMS.make) || '',
      model: params.get(VEHICLE_FILTER_PARAMS.model) || '',
      year: Number(params.get(VEHICLE_FILTER_PARAMS.year)) || 0,
    };
    const remembered = fromUrl.make && fromUrl.model ? fromUrl : readVehicle();
    const makes = Object.keys(this.#index).sort();

    this.#fill(this.refs.make, makes, this.dataset.makeLabel, remembered?.make);

    if (remembered && this.#index[remembered.make]) {
      this.refs.make.value = remembered.make;
      this.#fillModels(remembered.model);
      if (remembered.year) this.refs.year.value = String(remembered.year);
    }

    this.#syncSubmit();
  }

  /** @param {string} [selected] */
  #fillModels(selected) {
    const models = Object.keys(this.#index[this.refs.make.value] ?? {}).sort();
    this.#fill(this.refs.model, models, this.dataset.modelLabel, selected);
    this.#fillYears();
  }

  #fillYears() {
    const years = this.#index[this.refs.make.value]?.[this.refs.model.value] ?? [];
    this.#fill(this.refs.year, years, this.dataset.yearLabel);
    this.#syncSubmit();
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {(string | number)[]} values
   * @param {string} [placeholder]
   * @param {string} [selected]
   */
  #fill(select, values, placeholder, selected) {
    const options = [new Option(placeholder || '', '')];
    for (const value of values) options.push(new Option(String(value), String(value)));

    select.replaceChildren(...options);
    select.disabled = values.length === 0;
    if (selected && values.map(String).includes(selected)) select.value = selected;
  }

  /**
   * Clears all three selects back to their placeholders. Called by the
   * vehicle bar's "Clear" — a storage event won't fire in the same tab,
   * so the bar has to reach the finder directly.
   */
  reset() {
    this.refs.make.value = '';
    this.#fillModels();
  }

  #onMakeChange = () => this.#fillModels();

  #onModelChange = () => this.#fillYears();

  /**
   * The button stays live the whole time — a greyed submit next to three
   * empty dropdowns read as "no button here". Guidance happens on click
   * instead: an incomplete pick focuses the first field still missing.
   * Year is required only when the chosen model actually has year data;
   * four models currently have none, and blocking those shoppers behind
   * an empty dropdown would be a dead end.
   */
  #syncSubmit = () => {
    this.refs.submit.disabled = false;
  };

  #onSubmit = (event) => {
    event.preventDefault();

    const { make, model, year } = this.refs;
    if (!make.value) {
      make.focus();
      return;
    }
    if (!model.value) {
      model.focus();
      return;
    }
    const years = this.#index[make.value]?.[model.value] ?? [];
    if (years.length > 0 && !year.value) {
      year.focus();
      return;
    }

    const vehicle = {
      make: make.value,
      model: model.value,
      year: Number(year.value) || 0,
    };

    writeVehicle(vehicle);
    window.location.href = fitmentUrl(this.#collectionUrl, vehicle);
  };
}

if (!customElements.get('tt-vehicle-finder')) {
  customElements.define('tt-vehicle-finder', VehicleFinder);
}
